/* eslint-disable react/no-unescaped-entities */
import React from 'react';
import { PrivateLayout, PV_THEME } from '../PrivateLayout';

const T = PV_THEME;

function Numeral({ n, color }) {
  return (
    <div
      className="font-mono select-none"
      style={{ fontSize: 80, lineHeight: 0.85, color: `${color}18`, marginBottom: -16 }}
    >
      {n}
    </div>
  );
}

function SectionLabel({ text }) {
  return (
    <div
      className="font-mono text-[9px] tracking-[0.25em] uppercase mb-5"
      style={{ color: T.textFaint }}
    >
      {text}
    </div>
  );
}

function Rule({ color, width = 60 }) {
  return (
    <div className="flex justify-center my-12">
      <div style={{ width, height: 2, background: color, opacity: 0.5 }} />
    </div>
  );
}

function DropCap({ letter, color }) {
  return (
    <span
      className="float-left font-mono mr-2.5 mt-1"
      style={{ fontSize: 56, lineHeight: 0.75, color }}
    >
      {letter}
    </span>
  );
}

function PullQuote({ children, borderColor }) {
  return (
    <div
      className="font-mono text-[15px] md:text-[18px] tracking-[0.05em] text-center py-8 my-10"
      style={{
        color: T.text,
        borderTop: `1px solid ${borderColor}`,
        borderBottom: `1px solid ${borderColor}`,
        lineHeight: 1.55,
      }}
    >
      {children}
    </div>
  );
}

function Gold({ children }) {
  return (
    <span
      style={{ color: T.accent, textShadow: '0 0 8px rgba(245,166,35,0.4)' }}
    >
      {children}
    </span>
  );
}

const body = 'font-sans leading-[2] mb-6';
const bodyStyle = { fontSize: 13, color: '#a0a0b0' };
const emphStyle = { ...bodyStyle, color: '#c8c8d4', fontStyle: 'italic' };

export default function ManifestoSection() {
  return (
    <PrivateLayout eyebrowLabel="The Trust Manifesto" testid="pv-manifesto">
      {/* Hero */}
      <div className="relative overflow-hidden pt-16 pb-12 px-4 text-center">
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at 50% 0%, rgba(245,166,35,0.05) 0%, transparent 70%)',
          }}
        />
        <p
          className="relative z-10 font-mono text-[10px] tracking-[0.4em] uppercase mb-6"
          style={{ color: T.accent, textShadow: '0 0 10px rgba(245,166,35,0.4)' }}
        >
          INFINITYPAX · TRUSTFRAME · A MANIFESTO
        </p>
        <h1
          className="relative z-10 font-mono uppercase mb-4 mx-auto"
          style={{
            fontSize: 'clamp(26px, 6vw, 44px)',
            letterSpacing: '0.15em',
            lineHeight: 1.2,
            maxWidth: 700,
            color: T.text,
            textShadow: '0 0 6px rgba(255,255,255,0.2)',
          }}
        >
          The Freedom<br />to Create with<br />
          <span style={{ color: T.accent, textShadow: '0 0 10px rgba(245,166,35,0.4)', fontStyle: 'italic' }}>
            Abandon
          </span>
        </h1>
        <p
          className="relative z-10 font-sans text-[12px] sm:text-[13px] max-w-[500px] mx-auto italic leading-[1.8]"
          style={{ color: T.textMuted }}
        >
          On trust, regulation, and the liberation of art<br />
          in the age of the blockchain
        </p>
        <div className="w-px h-12 mx-auto mt-8 opacity-30" style={{ background: T.accent }} />
      </div>

      {/* Body */}
      <article className="max-w-[680px] mx-auto px-6 pb-24">
        {/* I */}
        <div className="scroll-mt-20">
          <Numeral n="I" color={T.accent} />
          <SectionLabel text="What TrustFrame Is" />
          <p className={body} style={bodyStyle}>
            <DropCap letter="T" color={T.accent} />
            he art market transacted $57.5 billion in 2024. Forty million transactions. Twenty-two per cent of dealer sales conducted online. Forty-six per cent of those online sales going to buyers who had never purchased from that gallery before. A market that once operated on handshakes in Mayfair back rooms is now being conducted across screens, continents, and currencies — and it has no trust infrastructure.
          </p>
          <p className={body} style={bodyStyle}>
            TrustFrame is that infrastructure. Not a marketplace. Not a gallery CRM. Not a blockchain art registry. Not an analytics dashboard. It is the trust layer that sits beneath all of them — the invisible connective tissue that makes provenance verifiable, compliance automatic, transactions secure, and records immutable.
          </p>
          <p className={body} style={bodyStyle}>
            Four modules. One architecture. <Gold>Comply</Gold> automates AML and KYC across every jurisdiction. <Gold>Verify</Gold> runs AI-assisted provenance research across thirty-five million auction records. <Gold>Transact</Gold> provides secure escrow for private sales with configurable confidentiality. <Gold>Attest</Gold> writes verified records to a permissioned ledger that galleries, auction houses, and collectors can selectively share from.
          </p>
          <p className={body} style={bodyStyle}>
            The design principle — conceived by Bashiru Lawal-Shardow out of the Royal College of Art's Service Design programme — is invisibility. The best trust infrastructure is the kind you never notice. It should feel like nothing changed. The gallery still sells the way it sells. The collector still buys the way they buy. The advisor still advises. But beneath every interaction, the provenance is verified, the parties are compliant, the payment is escrowed, and the record is permanent.
          </p>
          <PullQuote borderColor={T.accent}>
            The art market does not need another marketplace.<br />
            It needs a reason to trust the marketplaces it already has.
          </PullQuote>
        </div>

        <Rule color={T.accent} />

        {/* II */}
        <div>
          <Numeral n="II" color={T.green} />
          <SectionLabel text="Why Regulation Is Liberation" />
          <p className={body} style={bodyStyle}>
            <DropCap letter="T" color={T.green} />
            here is a persistent myth in the art world that regulation is the enemy of creativity. That compliance stifles. That transparency kills the mystery on which the market depends. This is not merely wrong — it is precisely backwards.
          </p>
          <p className={body} style={bodyStyle}>
            Opacity does not protect artists. It protects intermediaries. It protects the gatekeepers who profit from information asymmetry. It protects the launderers who, as HMRC's £2.3 million in fines across 408 businesses in six months makes clear, have found the unregulated art market a convenient vehicle. When France levied its first-ever AML penalties on art galleries in 2023, it was not attacking culture. It was defending it — defending the market's integrity so that the people who actually create, collect, and curate art can do so without being complicit in someone else's financial crime.
          </p>
          <p className={body} style={bodyStyle}>
            Regulation, properly designed, is the foundation of freedom. When a gallery knows that every buyer has been verified, it can sell with confidence. When a collector knows that the provenance is AI-verified against thirty-five million records, they can buy with conviction. When an artist knows that their sale is recorded on a permanent ledger, they can create without worrying that their work will disappear into an opaque secondary market they can never trace.
          </p>
          <p className={body} style={emphStyle}>
            The question is not whether the art market will be regulated. HMRC, the CNS, the SEC, and the EU's Anti-Money Laundering Directives have already answered that. The question is whether regulation will be imposed on the market from outside — clumsily, expensively, and without understanding its culture — or whether the market will build its own trust infrastructure from within.
          </p>
          <p className={body} style={bodyStyle}>
            Trustframe is the latter. It is the art market regulating itself — with intelligence, with discretion, and with the graduated disclosure that preserves the confidentiality on which the trade legitimately depends. A collector can prove compliance without revealing their identity. A gallery can verify provenance without exposing the seller. The trust is real. The privacy is intact.
          </p>
          <PullQuote borderColor={T.green}>
            Opacity protects intermediaries.<br />Transparency protects artists.
          </PullQuote>
        </div>

        <Rule color={T.green} />

        {/* III */}
        <div>
          <Numeral n="III" color={T.blue} />
          <SectionLabel text="Art, Design, and Music Are Cousins" />
          <p className={body} style={bodyStyle}>
            <DropCap letter="W" color={T.blue} />
            e have been trained to think in categories. Art is what hangs on walls. Design is what makes things functional. Music is what fills the room between them. This taxonomy served the twentieth century. It does not serve us now.
          </p>
          <p className={body} style={bodyStyle}>
            The new generation of collectors does not distinguish between a Richter painting, a Virgil Abloh chair, and a Floating Points pressing. They understand — instinctively, because they grew up in a world where these things coexisted on the same screen — that creative expression is a single continuum. The gallery, the studio, the club, the archive, the boutique — these are different rooms in the same house.
          </p>
          <p className={body} style={bodyStyle}>
            This is why Trustframe sits inside INFINITYPAX, not inside a gallery CRM company. INFINITYPAX's thesis is that intelligence infrastructure — PAXOS — can be applied across any domain where trust, verification, and secure transactions matter. Art is one domain. Hospitality is another. Luxury mobility is another. The thread that connects them is not the objects being transacted but the people transacting them.
          </p>
          <p className={body} style={emphStyle}>
            Muriel and Freddy Salem's Cranford Collection — seven hundred works, Chipperfield-renovated, Bridget Riley next to Christopher Wool next to Alice Neel — is not a collection of paintings. It is a designed environment. The curation is a form of composition. The rotation every eighteen months is a form of rhythm.
          </p>
          <p className={body} style={bodyStyle}>
            Yuval Brisker understood this when he co-founded Gertrude — an app named after Gertrude Stein, whose Paris salon was simultaneously a gallery, a literary venue, a music room, and a social infrastructure. Brisker's own career traces the same arc: from architecture to enterprise software to embedded finance to art technology. The categories dissolve. What remains is the practice of making systems that enable creativity.
          </p>
          <p className={body} style={bodyStyle}>
            And Bashiru Lawal-Shardow understood it at the Royal College of Art, where he studied Service Design — a discipline that refuses the distinction between the aesthetic and the functional. Service design's core insight — that the most powerful systems are the ones you never see — is Trustframe's founding principle.
          </p>
          <PullQuote borderColor={T.blue}>
            Art, design, and music are not separate disciplines.<br />
            They are dialects of the same language.
          </PullQuote>
        </div>

        <Rule color={T.blue} />

        {/* IV */}
        <div>
          <Numeral n="IV" color={T.accent} />
          <SectionLabel text="The New Generation Arrives" />
          <p className={body} style={bodyStyle}>
            <DropCap letter="T" color={T.accent} />
            he Art Basel report tells a story that the headline numbers obscure. Yes, the market contracted. But forty-four per cent of dealer clients in 2024 were new buyers. The sub-$50,000 segment expanded. Online sales to first-time buyers surged to forty-six per cent. The art market is not shrinking — it is being rebuilt from below, by people who have never set foot in a Mayfair gallery and do not intend to.
          </p>
          <p className={body} style={bodyStyle}>
            These new collectors are younger, more digitally fluent, and more diverse than any generation before them. They buy on Instagram. They discover art through Gertrude. They expect the same transparency from a gallery that they get from a fintech. They do not accept the premise that a $50,000 purchase should require more faith than a $50 one.
          </p>
          <p className={body} style={bodyStyle}>
            At the top of the wealth pyramid, $5.5 trillion is about to change hands as billionaires in their seventies transfer wealth to heirs who grew up on Spotify, not Sotheby's. Fifty-two per cent of new billionaire wealth in 2024 was inherited, not earned — a structural shift that will reshape not just how art is bought but what art is valued, by whom, and through what channels.
          </p>
          <p className={body} style={bodyStyle}>
            The galleries arriving are different too. Ceysson & Bénétière opening in Tokyo. Massimo de Carlo in Seoul. Perrotin in Los Angeles. The art market is decentralising — geographically, generationally, and culturally. And decentralised markets need shared infrastructure more than centralised ones do.
          </p>
          <p className={body} style={emphStyle}>
            This is the market Trustframe is built for. Not the market of ten years ago, where a hundred galleries in three cities served a thousand collectors. The market of ten years from now, where ten thousand galleries across fifty cities serve ten million collectors — most of whom have never met their dealer in person, and all of whom need to trust that what they are buying is what it claims to be.
          </p>
        </div>

        <Rule color={T.accent} />

        {/* V */}
        <div>
          <Numeral n="V" color={T.green} />
          <SectionLabel text="The Blockchain Must Be Freedom" />
          <p className={body} style={bodyStyle}>
            <DropCap letter="T" color={T.green} />
            he blockchain was supposed to liberate art. It was supposed to make provenance permanent, ownership transparent, and secondary market royalties automatic. Instead, it became a speculative casino. The NFT boom of 2021 generated billions in trading volume and almost nothing of lasting cultural value.
          </p>
          <p className={body} style={bodyStyle}>
            This is not a failure of the technology. It is a failure of intention. The blockchain was deployed to financialise art — to turn paintings into securities, collections into portfolios, and taste into yield. It served the logic of capital, not the logic of creation.
          </p>
          <p className={body} style={bodyStyle}>
            We believe the blockchain must serve a different purpose. Not financialisation but attestation. Not speculation but permanence. Not ownership fragmentation but provenance integrity. The blockchain's real gift to art is not that it can turn a JPEG into an asset class. It is that it can make the history of a physical artwork — who made it, who owned it, where it was shown, how it was verified — indelible.
          </p>
          <p className={body} style={emphStyle}>
            Trustframe's Attest module uses this principle. Not a public blockchain — a permissioned ledger. Institutionally trusted. Selectively shareable. The gallery writes the provenance. The auction house writes the sale. The conservator writes the condition. The collector writes the loan. Each entry is verified. Each entry is permanent. And the artist — for the first time in the history of the art market — can trace their work's journey through the world.
          </p>
          <p className={body} style={bodyStyle}>
            This is what the blockchain should have been from the beginning: not a casino but a cathedral. Not a mechanism for extracting value from art but a structure for preserving the truth of it. When the infrastructure of trust is solid, artists are free to make work that is fragile, provisional, experimental, and strange.
          </p>
          <div
            className="font-mono text-center py-8 my-10 italic"
            style={{
              fontSize: 20,
              letterSpacing: '0.05em',
              color: T.accent,
              textShadow: '0 0 12px rgba(245,166,35,0.4)',
              borderTop: `1px solid ${T.accent}`,
              borderBottom: `1px solid ${T.accent}`,
              lineHeight: 1.55,
            }}
          >
            They can create freely,<br />with abandon.
          </div>
        </div>

        <Rule color={T.accent} width={100} />

        {/* Coda */}
        <div className="pb-10">
          <Numeral n="—" color={T.textMuted} />
          <SectionLabel text="Coda" />
          <p className={body} style={bodyStyle}>
            We are not building Trustframe because the art market is broken. We are building it because the art market is changing — faster and more profoundly than at any point since the emergence of the gallery system in the nineteenth century. The new generation of collectors, artists, and galleries does not need to be taught to trust. They need infrastructure that makes trust automatic.
          </p>
          <p className={body} style={bodyStyle}>
            Design, music, and art are cousins. They share a grammar. They share an audience. They share a sensibility that the best things in life are the ones where the effort is invisible and the experience is everything. Trustframe is built on that principle. The compliance is invisible. The verification is invisible. The escrow is invisible. The ledger is invisible. What is visible is the art — and the freedom of the people who make it, sell it, collect it, and live with it.
          </p>
          <p className={body} style={{ ...bodyStyle, color: T.accent, fontStyle: 'italic' }}>
            The blockchain is not a technology. It is a promise: that truth, once written, cannot be unwritten. We intend to keep that promise — not for the financiers, but for the artists. Not for the speculators, but for the collectors who hang work on their walls and rotate it every eighteen months because living with art is a practice, not a position.
          </p>
          <p className={body} style={bodyStyle}>
            This is our manifesto. Trust is the infrastructure. Regulation is the liberation. The blockchain is the permanence. And the purpose of all of it — all of it — is to clear the ground so that the people who make things can make them freely, with abandon, and know that the world they send them into will take care of them.
          </p>

          <div
            className="mt-14 pt-8 flex flex-col sm:flex-row justify-between gap-6"
            style={{ borderTop: `1px solid ${T.border}` }}
          >
            <div>
              <div className="font-mono" style={{ fontSize: 12, color: T.text }}>
                Moses Kityamuwesi Kisubika
              </div>
              <div className="font-mono mt-1" style={{ fontSize: 9, color: T.textMuted }}>
                Global Creative Director · INFINITYPAX
              </div>
              <div className="font-mono mt-4" style={{ fontSize: 12, color: T.text }}>
                Bashiru Lawal-Shardow
              </div>
              <div className="font-mono mt-1" style={{ fontSize: 9, color: T.textMuted }}>
                Co-Founder · Trustframe · RCA Fellow
              </div>
            </div>
            <div className="sm:text-right space-y-1 font-mono" style={{ fontSize: 9, color: T.textFaint }}>
              <div>INFINITYPAX · PAXOS Intelligence</div>
              <div>Pleasurealm Holdings</div>
              <div>March 2026</div>
            </div>
          </div>
        </div>
      </article>
    </PrivateLayout>
  );
}
